/**
 * Append-only palette of interned, immutable `VoxelData` (docs/large-volume-plan.md §2.1).
 *
 * One palette lives per document lineage: it is created with the first grid and shared through
 * `asMutable()`. Ids are never reused within a session, so every historical grid version stays
 * valid. Interning keys on a numeric tuple, never on a per-cell string (`usesSurfaceOrientation`
 * allocates `{...voxel, face}` per cell, so object identity cannot be relied on either).
 *
 * The palette deliberately knows nothing about the voxel registry: `typeIndexOf` is a
 * palette-local index of the type string, and renderers build their own LUTs from `items`.
 */
import { isVoxelDensity, RENAMED_VOXEL_TYPES, VOXEL_MATERIALS, type Face, type VoxelData } from '@/voxelTypes'

const FACE_INDEX: Record<string, number> = { 'x+': 1, 'x-': 2, 'y+': 3, 'y-': 4, 'z+': 5, 'z-': 6 }
const MATERIAL_INDEX = new Map<string, number>(VOXEL_MATERIALS.map((m, i) => [m, i + 1]))
// Frozen entries catch any code that mutates a returned voxel (plan §8); skipped in production builds.
const FREEZE_ITEMS = !!import.meta.env?.DEV

// Tuple key layout (all fields non-negative integers, total < 2^53):
//   typeIdx * 2^37 + densityCode * 2^34 + colorCode * 2^9 + faceIdx * 2^6 + topFaceIdx * 2^3 + materialIdx
// colorCode = 0 when color is undefined, else color + 1 (needs 25 bits for 24-bit colors).
// densityCode = 0 when density is undefined, else the density (1..6, 3 bits).
const COLOR_SHIFT = 2 ** 9
const DENSITY_SHIFT = 2 ** 34
const TYPE_SHIFT = 2 ** 37
const MAX_TYPE_INDEX = 2 ** 16 - 1

export class VoxelPalette {
  /** items[0] is always undefined (id 0 = empty cell). Entries are frozen in dev builds. */
  readonly items: (Readonly<VoxelData> | undefined)[] = [undefined]
  private readonly typeNames: string[] = []
  private readonly typeIndexByName = new Map<string, number>()
  private readonly typeIndexById: number[] = [-1]
  private readonly idByTupleKey = new Map<number, number>()
  private readonly idByStringKey = new Map<string, number>()
  private readonly idByObject = new WeakMap<VoxelData, number>()
  private lastSeen: VoxelData | null = null
  private lastId = 0

  /** Number of ids including the empty id 0. */
  get length(): number {
    return this.items.length
  }

  /** Distinct type strings seen so far, indexed by `typeIndexOf`. */
  get types(): readonly string[] {
    return this.typeNames
  }

  /** Interns `v` and returns its palette id (>= 1). Equal values share an id. */
  intern(v: VoxelData): number {
    if (v === this.lastSeen) return this.lastId
    let id = this.idByObject.get(v)
    if (id === undefined) {
      id = this.internSlow(v)
      this.idByObject.set(v, id)
    }
    this.lastSeen = v
    this.lastId = id
    return id
  }

  /** Id for an already-interned value, or 0 if it has never been interned. Does not intern. */
  idOf(v: VoxelData): number {
    if (v === this.lastSeen) return this.lastId
    const byObj = this.idByObject.get(v)
    if (byObj !== undefined) return byObj
    const tuple = this.tupleKey(v)
    if (tuple !== null) return this.idByTupleKey.get(tuple) ?? 0
    return this.idByStringKey.get(this.stringKey(v)) ?? 0
  }

  /** Palette-local index of the type string of `id` (-1 for id 0). */
  typeIndexOf(id: number): number {
    return this.typeIndexById[id] ?? -1
  }

  /** Type string for a palette-local type index. */
  typeNameOf(typeIndex: number): string | undefined {
    return this.typeNames[typeIndex]
  }

  private internSlow(v: VoxelData): number {
    // A renamed type (saved before the rename) is stored under its new name.
    const renamed = RENAMED_VOXEL_TYPES[v.type]
    if (renamed !== undefined) return this.intern({ ...v, type: renamed })
    const tuple = this.tupleKey(v)
    if (tuple !== null) {
      const existing = this.idByTupleKey.get(tuple)
      if (existing !== undefined) return existing
      const id = this.append(v)
      this.idByTupleKey.set(tuple, id)
      return id
    }
    const sk = this.stringKey(v)
    const existing = this.idByStringKey.get(sk)
    if (existing !== undefined) return existing
    const id = this.append(v)
    this.idByStringKey.set(sk, id)
    return id
  }

  private append(v: VoxelData): number {
    const id = this.items.length
    const copy: VoxelData = { type: v.type }
    if (v.color !== undefined) copy.color = v.color
    if (v.face !== undefined) copy.face = v.face
    if (v.topFace !== undefined) copy.topFace = v.topFace
    if (v.material !== undefined) copy.material = v.material
    if (v.density !== undefined) copy.density = v.density
    this.items.push(FREEZE_ITEMS ? Object.freeze(copy) : copy)
    this.typeIndexById.push(this.typeIndex(v.type))
    return id
  }

  private typeIndex(type: string): number {
    let idx = this.typeIndexByName.get(type)
    if (idx === undefined) {
      idx = this.typeNames.length
      this.typeNames.push(type)
      this.typeIndexByName.set(type, idx)
    }
    return idx
  }

  /** Numeric tuple key, or null when a field is outside the packable range (then a string key is used). */
  private tupleKey(v: VoxelData): number | null {
    const c = v.color
    let colorCode: number
    if (c === undefined || c === null) colorCode = 0
    else if (Number.isInteger(c) && c >= 0 && c <= 0xffffff) colorCode = c + 1
    else return null
    const faceIdx = faceIndex(v.face)
    const topIdx = faceIndex(v.topFace)
    if (faceIdx < 0 || topIdx < 0) return null
    let materialIdx = 0
    if (v.material !== undefined) {
      const m = MATERIAL_INDEX.get(v.material)
      if (m === undefined) return null
      materialIdx = m
    }
    let densityCode = 0
    if (v.density !== undefined) {
      if (!isVoxelDensity(v.density)) return null
      densityCode = v.density
    }
    const typeIdx = this.typeIndex(v.type)
    if (typeIdx > MAX_TYPE_INDEX) return null
    return typeIdx * TYPE_SHIFT + densityCode * DENSITY_SHIFT + colorCode * COLOR_SHIFT + faceIdx * 64 + topIdx * 8 + materialIdx
  }

  private stringKey(v: VoxelData): string {
    return `${v.type}|${v.color ?? ''}|${v.face ?? ''}|${v.topFace ?? ''}|${v.material ?? ''}|${v.density ?? ''}`
  }
}

function faceIndex(face: Face | undefined): number {
  if (face === undefined) return 0
  const idx = FACE_INDEX[face]
  return idx === undefined ? -1 : idx
}
